import { globalTimer, TimerCallback } from './globalTimer';
import { logError } from './ApiHelper';

type SubscriptionTimerCallback = (caseReferenceId: string) => void;

interface SubscriptionTimer {
  caseReferenceId: string;
  expiresAt: number;
  unsubscribeCallback: () => void;
}

const subscriptionTimers: Map<string, SubscriptionTimer> = new Map();
let timerUnsubscribe: (() => void) | null = null;

const checkExpiredSubscriptions = (onExpire: SubscriptionTimerCallback) => {
  const now = Date.now();
  const expiredKeys: string[] = [];

  subscriptionTimers.forEach((timer, caseReferenceId) => {
    if (now >= timer.expiresAt) {
      expiredKeys.push(caseReferenceId);
    }
  });

  expiredKeys.forEach(caseReferenceId => {
    const timer = subscriptionTimers.get(caseReferenceId);
    if (timer) {
      try {
        onExpire(caseReferenceId);
        subscriptionTimers.delete(caseReferenceId);
      } catch (error) {
        logError(error, `Failed to handle expired subscription for ${caseReferenceId}`);
      }
    }
  });

  if (subscriptionTimers.size === 0 && timerUnsubscribe) {
    timerUnsubscribe();
    timerUnsubscribe = null;
    globalTimer.stop();
  }
};

export const subscriptionManager = {
  startTracking: (onExpire: SubscriptionTimerCallback): void => {
    if (!timerUnsubscribe) {
      const callback: TimerCallback = () => {
        checkExpiredSubscriptions(onExpire);
      };

      timerUnsubscribe = globalTimer.subscribe(callback);

      if (!globalTimer.isRunning()) {
        globalTimer.start(1000);
      }
    }
  },

  addSubscription: (
    caseReferenceId: string,
    expiresAt: number,
    onExpire: SubscriptionTimerCallback
  ): void => {
    if (!caseReferenceId || !expiresAt || typeof expiresAt !== 'number') {
      logError(
        new Error('Invalid subscription parameters'),
        `Invalid caseReferenceId: ${caseReferenceId} or expiresAt: ${expiresAt}`
      );
      return;
    }

    if (subscriptionTimers.has(caseReferenceId)) {
      subscriptionManager.removeSubscription(caseReferenceId);
    }

    const unsubscribeCallback = () => {
      subscriptionTimers.delete(caseReferenceId);
    };

    subscriptionTimers.set(caseReferenceId, {
      caseReferenceId,
      expiresAt,
      unsubscribeCallback
    });

    subscriptionManager.startTracking(onExpire);
  },

  removeSubscription: (caseReferenceId: string): void => {
    const timer = subscriptionTimers.get(caseReferenceId);
    if (timer) {
      timer.unsubscribeCallback();
    }
  },

  updateSubscription: (caseReferenceId: string, expiresAt: number): void => {
    const timer = subscriptionTimers.get(caseReferenceId);
    if (timer) {
      timer.expiresAt = expiresAt;
    }
  },

  hasSubscription: (caseReferenceId: string): boolean => {
    return subscriptionTimers.has(caseReferenceId);
  },

  getTimeRemaining: (caseReferenceId: string): number => {
    const timer = subscriptionTimers.get(caseReferenceId);
    if (!timer) return 0;

    const remaining = timer.expiresAt - Date.now();
    return Math.max(0, Math.floor(remaining / 1000));
  },

  clearAll: (): void => {
    subscriptionTimers.clear();

    if (timerUnsubscribe) {
      try {
        timerUnsubscribe();
      } catch (error) {
        logError(error, 'Error unsubscribing from global timer');
      } finally {
        timerUnsubscribe = null;
      }
    }
    globalTimer.stop();
  },

  getActiveSubscriptions: (): string[] => {
    return Array.from(subscriptionTimers.keys());
  }
};
